import { Draggable } from "@msuite/ui/dnd";
import { moment } from "@msuite/utils/date";
import { guardString } from "@msuite/utils/strings";
import { useTranslation } from "@msuite/ui/localization";
import { Weekdays } from "@msuite/ui/weekday-picker";
import type { CrewDefinitionListItem } from "@/hooks/use-crew-definitions";
import { CrewDefinitionTags } from "./crew-definition-tags";

/** Props Interface */
interface ManagerSlotCardProps {
	crewDefinition: CrewDefinitionListItem;
	managerSlot: CrewDefinitionListItem["_managerSlots"][number];
}

/**
 * One window the reader holds over a crew: the crew it is on, and the days it covers.
 *
 * The span is the slot's, not the crew's. A crew may stand for years while the window over it is a
 * fortnight, and the fortnight is the part that says when this manager may schedule it.
 *
 * The card is what gets dragged onto a ticket's bar, and it travels as the crew **and** the window
 * it came from. The window is what says the reader may place this crew at all, and only on these
 * days — so the drop can refuse a ticket outside it without a round trip to find out.
 *
 * The weekday strip is the other bound, and the one a reader cannot infer from a date span: a crew
 * is refused a stretch it would not work every day of.
 */
export function ManagerSlotCard({ crewDefinition, managerSlot }: ManagerSlotCardProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	// An open-ended window is the common case, so it reads as a start with an em dash rather than
	// inventing an end the row does not have.
	const span = `${moment(managerSlot.startDate).format("DD.MM.YYYY")} – ${
		managerSlot.endDate ? moment(managerSlot.endDate).format("DD.MM.YYYY") : "∞"
	}`;

	/** Render */
	return (
		<Draggable
			type="crew-definition"
			item={{ crewDefinition, managerSlot }}
			className="v-stack gap-2 card p-2"
		>
			<div className="v-stack gap-0 min-w-0">
				<p className="text-sm truncate">
					{guardString(crewDefinition.displayName, t("planning.crews.unnamed_crew"))}
				</p>
				<p className="label truncate">{span}</p>
			</div>
			<CrewDefinitionTags crewDefinition={crewDefinition} />
			{/* The days this crew turns up on, which is the third thing a drop is refused for after
			    the crew's span and the window above. Reading it off the card is what stops a drag
			    that was only ever going to be turned away. */}
			<Weekdays
				isReadonly
				size="xs"
				selectedDays={crewDefinition.weekdays}
			/>
		</Draggable>
	);
}
